/* eslint-disable no-underscore-dangle */
// Enums
import { ExportComparisonRequirements, ProposalExportRequirementsModalDisplayMode } from '@/enums/Comparison';

// Types
import AppRegion from '@/types/app-region';
import ComparisonHolding from '@/types/comparison-holding';
import ComparisonItem from '@/types/comparison-item';
import Comparison from '@/types/comparison';
import Dictionary from '@/types/dictionary';
import Holding from '@/types/holding';
import User from '@/types/user';
import Questionnaire from '@/types/questionnaire';
import { ComparisonItemCategory } from '@/redux/modules/types/comparison';
import { HoldingsData } from '@/redux/modules/comparison';
import { RiskProfileRule } from '@/types/risk-profiles';

// Utils
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { formatLocalizedString } from '@/utils/i18n';
import { getRiskProfileRuleData } from '@/utils/risk-profiles';
import { holdingsToComparisonItems } from '@/utils/comparison';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';
import { getPortfolioResolutionStrategy } from '@/utils/comparison/comparison-export-requirements';

export type RequirementEvaluator = {
  requirement: ExportComparisonRequirements;
  isValid: (params: {
    comparison?: Comparison;
    user?: User;
    holdingsCache?: HoldingsDataCache[];
    getItemHoldingsData?: (holdings: ComparisonHolding[]) => Promise<HoldingsData>;
  }) => boolean | Promise<boolean>;
};

export type ProcessedHoldings = ComparisonItem & { weight: number; isOffShelf: boolean; units?: number };

export type HoldingsDataCache = {
  _id: string;
  processedHoldings: ProcessedHoldings[];
  category: ComparisonItemCategory | undefined;
};

export const getHoldingsDataCache = async (
  comparison: Comparison,
  user: User,
  getItemHoldingsData?: (holdings: ComparisonHolding[]) => Promise<HoldingsData>,
): Promise<HoldingsDataCache[]> => {
  const appRegion: AppRegion = user?.appConfig?.appRegion || AppRegion.CAN;
  const portfolios = comparison.items.filter(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);

  const portfolioItems: {
    _id: string;
    processedHoldings: (ComparisonItem & { weight: number; isOffShelf: boolean })[];
    category: ComparisonItemCategory | undefined;
  }[] = [];

  // Fetch all portfolio holdings data in parallel
  await Promise.all(
    portfolios.map(async item => {
      // Only fetch if we have holdings
      if (item.holdings && item.holdings.length && getItemHoldingsData) {
        const data = await getItemHoldingsData(item.holdings);

        // Pre-process the data to avoid repeated transformations
        const { alts, benchmarks, funds, stocks } = data;
        const dataDict = [...alts, ...funds, ...stocks].reduce((acc, d) => {
          acc[d._id] = d;
          return acc;
        }, {} as Dictionary<Holding>);

        // Process the holdings once
        const processedHoldings = holdingsToComparisonItems(item.holdings, dataDict, appRegion, benchmarks).map(h => ({
          ...h,
          weight: item.holdings.find(holding => holding.id === h._id)?.weight || 0,
          units: item.holdings.find(holding => holding.id === h._id)?.units || 0,
          isOffShelf: !h.flags || h.flags.length === 0,
        }));
        portfolioItems.push({
          _id: item._id,
          processedHoldings,
          category: item.category,
        });
      }
    }),
  );

  return portfolioItems;
};

/**
 * Determines whether a given comparison item falls outside the specified risk profile rules.
 *
 * @param rules - An array of risk profile rules to check against.
 * @param item - The comparison item to validate.
 * @param user - The user context used in the risk profile rule evaluation.
 * @returns `true` if any of the rules result in a truthy adjustment value
 */
export const isItemOutsideRiskProfileRange = (rules: RiskProfileRule[], item: ComparisonItem, user?: User): boolean => {
  if (!user || !rules || rules.length === 0) {
    return false;
  }
  return rules.some((rule: RiskProfileRule) => {
    const riskProfileRuleData = getRiskProfileRuleData(rule, item, undefined, user);
    return riskProfileRuleData.some(riskProfileCalc => !!riskProfileCalc.adjustment);
  });
};

export const requirementEvaluators: Promise<RequirementEvaluator[]> | RequirementEvaluator[] = [
  {
    requirement: ExportComparisonRequirements.IS_COMPARISON_HAS_RISK_PROFILE,
    isValid: ({ comparison }) => {
      if (!comparison) {
        return false;
      }
      return !!comparison.riskProfile;
    },
  },
  {
    requirement: ExportComparisonRequirements.IS_EACH_PORTFOLIOS_HAS_RISK_PROFILE,
    isValid: ({ comparison }) => {
      if (!comparison) {
        return false;
      }
      return comparison.items.every(item => {
        if (item.type !== COMPARISON_ITEM_TYPES.PORTFOLIO) return true;
        if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && !!item.riskProfile) return true;
        if (comparison.riskProfile) return true;
        return false;
      });
    },
  },
  {
    requirement: ExportComparisonRequirements.IS_EACH_PORTFOLIOS_CURRENT_OR_RECOMMENDED,
    isValid: ({ comparison, user }) => {
      if (!comparison || !user) {
        return false;
      }

      const strategy = getPortfolioResolutionStrategy(user, comparison);
      return strategy.areAllPortfoliosAssigned(comparison);
    },
  },
  {
    requirement: ExportComparisonRequirements.IS_RECOMMENDED_PORTFOLIOS_HAS_RISK_PROFILE,
    isValid: ({ comparison, user }) => {
      if (!comparison || !user) {
        return false;
      }
      if (comparison.riskProfile) {
        return true;
      }

      const strategy = getPortfolioResolutionStrategy(user, comparison);
      const recommendedPortfolios = strategy.getRecommendedPortfolios(comparison);

      return recommendedPortfolios.every(item => !!item.riskProfile);
    },
  },
  {
    requirement: ExportComparisonRequirements.ARE_PORTFOLIOS_IN_RANGE,
    isValid: ({ comparison, user }) => {
      if (!comparison || !user) {
        return false;
      }

      const portfolios = comparison.items.filter(item => item.type === COMPARISON_ITEM_TYPES.PORTFOLIO);

      if (comparison.riskProfile && comparison.riskProfile.rules) {
        return portfolios.every(item => !isItemOutsideRiskProfileRange(comparison.riskProfile!.rules, item, user));
      }

      return portfolios.every(item => {
        if (item.riskProfile && item.riskProfile.rules) {
          return !isItemOutsideRiskProfileRange(item.riskProfile.rules, item, user);
        }
        return true;
      });
    },
  },
  {
    requirement: ExportComparisonRequirements.ARE_RECOMMENDED_PORTFOLIOS_IN_RANGE,
    isValid: ({ comparison, user }) => {
      if (!comparison || !user) {
        return false;
      }

      const strategy = getPortfolioResolutionStrategy(user, comparison);
      const recommendedPortfolios = strategy.getRecommendedPortfolios(comparison);

      if (comparison.riskProfile && comparison.riskProfile.rules) {
        return recommendedPortfolios.every(item => !isItemOutsideRiskProfileRange(comparison.riskProfile!.rules, item, user));
      }

      return recommendedPortfolios.every(item => {
        if (item.riskProfile && item.riskProfile.rules) {
          return !isItemOutsideRiskProfileRange(item.riskProfile.rules, item, user);
        }
        return true;
      });
    },
  },
  {
    requirement: ExportComparisonRequirements.RECOMMENDED_PORTFOLIO_CANNOT_EXCEED_CURRENT_OFF_SHELF,
    isValid: async ({ holdingsCache, comparison, user }) => {
      if (!holdingsCache || !comparison || !user) {
        return false;
      }

      const strategy = getPortfolioResolutionStrategy(user, comparison);

      // Get portfolio IDs using strategy
      const recommendedPortfolioItems = strategy.getRecommendedPortfolios(comparison);
      const currentPortfolioItems = strategy.getCurrentPortfolios(comparison);

      const recommendedIds = new Set(recommendedPortfolioItems.map(item => item._id));
      const currentIds = new Set(currentPortfolioItems.map(item => item._id));

      // Filter holdings cache by IDs
      const recommendedPortfolios = holdingsCache.filter(cache => recommendedIds.has(cache._id));
      const currentPortfolios = holdingsCache.filter(cache => currentIds.has(cache._id));

      // If there are no current or recommended portfolios, the requirement is satisfied
      if (currentPortfolios.length === 0 || recommendedPortfolios.length === 0) {
        return true;
      }

      // get the offShelf holdings from the current portfolios
      const currentOffShelfHoldings = currentPortfolios.flatMap(item => item.processedHoldings.filter(holding => holding.isOffShelf));
      // get the offShelf holdings from the recommended portfolios
      const recommendedOffShelfHoldings = recommendedPortfolios.flatMap(item => item.processedHoldings.filter(holding => holding.isOffShelf));
      // if no holdings are off-shelf in the recommended portfolios, the requirement is satisfied

      if (recommendedOffShelfHoldings.length === 0) {
        return true;
      }

      const hasOffShelfHoldingsNotInCurrent = recommendedOffShelfHoldings.some(
        recommendedHolding => !currentOffShelfHoldings.some(currentHolding => currentHolding._id === recommendedHolding._id),
      );

      // if there are off-shelf holdings in the recommended portfolios but none in the current portfolio, the requirement is not satisfied
      if (hasOffShelfHoldingsNotInCurrent) {
        return false;
      }

      // returns true if all recommended off-shelf holdings are within the weight of the current off-shelf holdings
      return recommendedOffShelfHoldings.every(recommendedHolding => {
        const currentHolding = currentOffShelfHoldings.find(currHolding => currHolding._id === recommendedHolding._id);

        if (currentHolding?.units && recommendedHolding.units) {
          return currentHolding && recommendedHolding.units <= currentHolding.units;
        }

        return currentHolding && recommendedHolding.weight <= currentHolding.weight;
      });
    },
  },
  {
    requirement: ExportComparisonRequirements.RECOMMENDED_PORTFOLIO_CANNOT_CONTAIN_OFF_SHELF,
    isValid: async ({ holdingsCache, comparison, user }) => {
      if (!holdingsCache || !comparison || !user) {
        return false;
      }

      const strategy = getPortfolioResolutionStrategy(user, comparison);
      const recommendedPortfolioItems = strategy.getRecommendedPortfolios(comparison);
      const recommendedIds = new Set(recommendedPortfolioItems.map(item => item._id));

      const recommendedPortfolios = holdingsCache.filter((item: HoldingsDataCache) => recommendedIds.has(item._id));

      return recommendedPortfolios.every((item: HoldingsDataCache) => item.processedHoldings.every((holding: ProcessedHoldings) => !holding.isOffShelf));
    },
  },
];

/**
 * Used by the failed requirements
 * Map between the `ExportComparisonRequirements` which were failed
 * and the `ProposalExportRequirementsModalDisplayMode` as the display mode
 */
export const requirementDisplayMap: Record<ExportComparisonRequirements, ProposalExportRequirementsModalDisplayMode> = {
  [ExportComparisonRequirements.IS_COMPARISON_HAS_RISK_PROFILE]: ProposalExportRequirementsModalDisplayMode.MISSING_COMPARISON_RISK_PROFILE,
  [ExportComparisonRequirements.IS_EACH_PORTFOLIOS_HAS_RISK_PROFILE]: ProposalExportRequirementsModalDisplayMode.MISSING_PORTFOLIO_RISK_PROFILE,
  [ExportComparisonRequirements.IS_RECOMMENDED_PORTFOLIOS_HAS_RISK_PROFILE]:
    ProposalExportRequirementsModalDisplayMode.MISSING_RECOMMENDED_PORTFOLIOS_RISK_PROFILE,
  [ExportComparisonRequirements.ARE_PORTFOLIOS_IN_RANGE]: ProposalExportRequirementsModalDisplayMode.PORTFOLIO_RISK_PROFILE_OUTSIDE_RANGE,
  [ExportComparisonRequirements.ARE_RECOMMENDED_PORTFOLIOS_IN_RANGE]: ProposalExportRequirementsModalDisplayMode.RECOMMENDED_PORTFOLIOS_OUTSIDE_RANGE,
  [ExportComparisonRequirements.IS_EACH_PORTFOLIOS_CURRENT_OR_RECOMMENDED]: ProposalExportRequirementsModalDisplayMode.PORTFOLIO_NOT_CURRENT_OR_RECOMMENDED,
  [ExportComparisonRequirements.RECOMMENDED_PORTFOLIO_CANNOT_EXCEED_CURRENT_OFF_SHELF]:
    ProposalExportRequirementsModalDisplayMode.RECOMMENDED_PORTFOLIOS_EXCEEDS_CURRENT_OFF_SHELF,
  [ExportComparisonRequirements.RECOMMENDED_PORTFOLIO_CANNOT_CONTAIN_OFF_SHELF]:
    ProposalExportRequirementsModalDisplayMode.RECOMMENDED_PORTFOLIOS_CONTAINS_OFF_SHELF,
};

/**
 * Returns an array of export comparison requirements that are not met for the given comparison and user.
 *
 * @param comparison - The comparison object to validate.
 * @param user - The user whose config determines which requirements to validate.
 * @returns An array of failed `ExportComparisonRequirements`.
 */
export const getFailedRequirements = async (
  comparison: Comparison,
  user: User,
  getItemHoldingsData?: (holdings: ComparisonHolding[]) => Promise<HoldingsData>,
): Promise<ExportComparisonRequirements[]> => {
  const config = user.exportComparisonRequirements || [];

  if (!comparison || !config) {
    return [];
  }

  const requirementEvaluatorsToCheck = requirementEvaluators.filter(({ requirement }) => config.includes(requirement));

  const needsHoldingsData = requirementEvaluatorsToCheck.some(
    ({ requirement }) =>
      requirement === ExportComparisonRequirements.RECOMMENDED_PORTFOLIO_CANNOT_EXCEED_CURRENT_OFF_SHELF ||
      requirement === ExportComparisonRequirements.RECOMMENDED_PORTFOLIO_CANNOT_CONTAIN_OFF_SHELF,
  );

  let holdingsCache: HoldingsDataCache[];
  if (needsHoldingsData && getItemHoldingsData) {
    holdingsCache = await getHoldingsDataCache(comparison, user, getItemHoldingsData);
  }

  const resultsWithValidity = await Promise.all(
    requirementEvaluatorsToCheck.map(async ({ requirement, isValid }) => {
      const isValidResult = await isValid({ comparison, user, holdingsCache });
      return { requirement, isValid: isValidResult };
    }),
  );

  const failedRequirements = resultsWithValidity.filter(result => !result.isValid).map(result => result.requirement);

  return failedRequirements;
};

/**
 * Retrieves the names of comparison items of a specific type that fall outside the risk profile range.
 * For portfolios, excludes recommended portfolios as they are handled separately.
 * Uses the portfolio resolution strategy to determine which portfolios to check.
 *
 * @param comparison - The comparison containing the items.
 * @param user - The user used to evaluate risk profile rules.
 * @param type - The comparison item type to filter and evaluate.
 * @returns An array of localized item names that are out of range.
 */
export const getItemNameOutsideRiskProfileRange = (comparison: Comparison, user: User, type: COMPARISON_ITEM_TYPES): string[] => {
  const strategy = getPortfolioResolutionStrategy(user, comparison);

  return comparison.items.reduce<string[]>((acc, item) => {
    if (item.type !== type) return acc;

    // Skip recommended portfolios (handled separately)
    if (type === COMPARISON_ITEM_TYPES.PORTFOLIO && strategy.isRecommendedPortfolio(comparison, item)) {
      return acc;
    }

    const rules = comparison.riskProfile?.rules || item.riskProfile?.rules;
    if (!rules) return acc;
    if (isItemOutsideRiskProfileRange(rules, item, user)) {
      acc.push(formatLocalizedString(item.name));
    }
    return acc;
  }, []);
};

/**
 * Retrieves the names of recommended portfolios that fall outside the risk profile range.
 * Uses the portfolio resolution strategy to determine which portfolios are recommended.
 *
 * @param comparison - The comparison containing the items.
 * @param user - The user used to evaluate risk profile rules.
 * @returns An array of localized recommended portfolio names that are out of range.
 */
export const getRecommendedPortfolioNamesOutsideRiskProfileRange = (comparison: Comparison, user: User): string[] => {
  const strategy = getPortfolioResolutionStrategy(user, comparison);
  const recommendedPortfolios = strategy.getRecommendedPortfolios(comparison);

  return recommendedPortfolios.reduce<string[]>((acc, item) => {
    const rules = comparison.riskProfile?.rules || item.riskProfile?.rules;
    if (!rules) return acc;
    if (isItemOutsideRiskProfileRange(rules, item, user)) {
      acc.push(formatLocalizedString(item.name));
    }
    return acc;
  }, []);
};

/**
 * Evaluates which display modes should be triggered based on out-of-range items in a comparison,
 * considering the enabled export comparison requirements.
 *
 * @param comparison - The comparison to evaluate.
 * @param user - The user context for permission and configuration checks.
 * @param config - A list of enabled `ExportComparisonRequirements` to evaluate.
 * @returns An object containing:
 *   - `displayModes`: UI indicators for out-of-range conditions.
 *   - `portfolioNamesOutsideRange`: Names of all out-of-range portfolios.
 */
export const getOutOfRangeData = async (
  comparison: Comparison,
  user: User,
  config: ExportComparisonRequirements[],
): Promise<{
  displayModes: ProposalExportRequirementsModalDisplayMode[];
  portfolioNamesOutsideRange: string[];
}> => {
  const displayModes: ProposalExportRequirementsModalDisplayMode[] = [];
  const names: string[] = [];

  const failedRequirements = await getFailedRequirements(comparison, user);

  if (
    config.includes(ExportComparisonRequirements.ARE_PORTFOLIOS_IN_RANGE) &&
    failedRequirements.includes(ExportComparisonRequirements.ARE_PORTFOLIOS_IN_RANGE)
  ) {
    const portfolioOutsideRange = getItemNameOutsideRiskProfileRange(comparison, user, COMPARISON_ITEM_TYPES.PORTFOLIO);
    names.push(...portfolioOutsideRange);
    if (portfolioOutsideRange.length > 0) {
      displayModes.push(ProposalExportRequirementsModalDisplayMode.PORTFOLIO_RISK_PROFILE_OUTSIDE_RANGE);
    }
  }

  if (
    config.includes(ExportComparisonRequirements.ARE_RECOMMENDED_PORTFOLIOS_IN_RANGE) &&
    failedRequirements.includes(ExportComparisonRequirements.ARE_RECOMMENDED_PORTFOLIOS_IN_RANGE)
  ) {
    const recommendedPortfolioOutsideRange = getRecommendedPortfolioNamesOutsideRiskProfileRange(comparison, user);
    names.push(...recommendedPortfolioOutsideRange);
    if (recommendedPortfolioOutsideRange.length > 0) {
      displayModes.push(ProposalExportRequirementsModalDisplayMode.RECOMMENDED_PORTFOLIOS_OUTSIDE_RANGE);
    }
  }

  return {
    displayModes,
    portfolioNamesOutsideRange: names,
  };
};

export const getItemsOutsideRiskProfileRange = async (
  comparison: Comparison,
  user: User,
): Promise<{
  displayModes: ProposalExportRequirementsModalDisplayMode[];
  portfolioNamesOutsideRange: string[];
}> => {
  if (!comparison || !user) {
    return {
      displayModes: [],
      portfolioNamesOutsideRange: [],
    };
  }
  const config = user.exportComparisonRequirements || [];
  return getOutOfRangeData(comparison, user, config);
};

/**
 * Checks all applicable risk profile requirements for a comparison and returns display modes
 * that should be shown in the UI for export validation.
 *
 * @param comparison - The comparison object to evaluate.
 * @param user - The user whose config is used to determine which requirements apply.
 * @returns An array of `ProposalExportRequirementsModalDisplayMode` representing failed rules;
 */
export const validateRiskProfileRequirementsForExport = async (
  comparison: Comparison,
  user: User,
  getItemHoldingsData?: (holdings: ComparisonHolding[]) => Promise<HoldingsData>,
): Promise<ProposalExportRequirementsModalDisplayMode[]> => {
  if (!comparison || !user) return [];
  const failedRules = await getFailedRequirements(comparison, user, getItemHoldingsData);
  return failedRules.map(failedRule => requirementDisplayMap[failedRule]);
};

export type FunctionParams = {
  comparison?: Comparison;
  user?: User;
  rationaleQuestionnaires?: Questionnaire[];
  rationaleCompleted?: boolean;
};
/**
 * An array of requirement evaluator objects, each responsible for validating a specific
 * export comparison requirement based on the comparison data.
 */
export const validateRationaleQuestionnaireRequirements = ({
  comparison,
  user,
  rationaleQuestionnaires,
  rationaleCompleted,
}: FunctionParams): ProposalExportRequirementsModalDisplayMode[] => {
  const proposalExportRequirements: ProposalExportRequirementsModalDisplayMode[] = [];

  const isRationaleQuestionnaireNotCompleted = rationaleQuestionnaires && rationaleQuestionnaires.length > 0 && !rationaleCompleted;
  const isRationaleQuestionnaireCompleted = rationaleQuestionnaires && rationaleQuestionnaires.length > 0 && rationaleCompleted;
  const hasSomeStocksInComparison = comparison && comparison.items.some(item => item.holdings.some(holding => holding.type === COMPARISON_ITEM_TYPES.STOCK));
  const hasPermissionRationaleQuestionnaireRequired = userHasPermissionEnabled(user, PERMISSIONS.RATIONALE_REQUIRED);
  const hasPermissionRationaleQuestionnaireWarning = userHasPermissionEnabled(user, PERMISSIONS.RATIONALE_WARNING);
  const hasSecuritiesWarningPermissionsEnabled = userHasPermissionEnabled(user, PERMISSIONS.SECURITIES_WARNING);
  const hasSecuritiesWarning = hasSomeStocksInComparison && hasSecuritiesWarningPermissionsEnabled;

  const requirementRules = [
    {
      condition: isRationaleQuestionnaireNotCompleted && hasPermissionRationaleQuestionnaireRequired,
      displayMode: ProposalExportRequirementsModalDisplayMode.ERROR,
    },
    {
      condition: isRationaleQuestionnaireNotCompleted && hasPermissionRationaleQuestionnaireWarning,
      displayMode: ProposalExportRequirementsModalDisplayMode.WARNING,
    },
    {
      condition: isRationaleQuestionnaireCompleted && hasSecuritiesWarningPermissionsEnabled && hasSomeStocksInComparison,
      displayMode: ProposalExportRequirementsModalDisplayMode.WARNING,
    },
    {
      condition: hasSecuritiesWarning,
      displayMode: ProposalExportRequirementsModalDisplayMode.SECURITY_WARNING,
    },
  ];

  requirementRules.forEach(rule => {
    if (rule.condition) proposalExportRequirements.push(rule.displayMode);
  });

  return proposalExportRequirements;
};
